import { formatDateLong, formatPrice } from "@/utils/format";
import { parseLineItem } from "@/utils/parseLineItem";
import type { Cart } from "@/utils/types/types";
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

const BRAND_TEAL = "#4AA0A0";
const GRAY_LIGHT = "#F1F5F9";
const GRAY_DARK = "#191919";
const GRAY_BORDER = "#E2E8F0";

const s = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 9,
    color: GRAY_DARK,
    paddingTop: 48,
    paddingBottom: 56,
    paddingHorizontal: 48,
  },

  // ── Page header ──────────────────────────────────────────────
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
  },
  brandName: {
    fontSize: 16,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 1,
    color: BRAND_TEAL,
  },
  logoImage: {
    width: 160,
    objectFit: "contain",
  },
  contactInfo: {
    marginTop: 5,
    gap: 2,
  },
  contactText: {
    fontSize: 8,
    color: GRAY_DARK,
  },
  headerRight: {
    alignItems: "flex-end",
    gap: 3,
  },
  docTitle: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: GRAY_DARK,
    marginBottom: 4,
  },
  metaRow: {
    flexDirection: "row",
    gap: 4,
  },
  metaLabel: {
    color: GRAY_DARK,
  },
  metaValue: {
    fontFamily: "Helvetica-Bold",
  },
  dividerTop: {
    borderBottomWidth: 2,
    borderBottomColor: BRAND_TEAL,
    marginBottom: 16,
  },

  // ── Section title ────────────────────────────────────────────
  sectionTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    marginBottom: 8,
  },

  // ── Item card ─────────────────────────────────────────────────
  // Outer wrapper — gets the bottom border
  itemCard: {
    borderBottomWidth: 1,
    borderBottomColor: GRAY_BORDER,
    paddingVertical: 14,
    paddingHorizontal: 8,
  },

  // Top zone: image + columns
  topZone: {
    flexDirection: "row",
    gap: 16,
    alignItems: "flex-start",
  },

  // Image
  itemImage: {
    width: 52,
    height: 52,
    objectFit: "contain",
    borderRadius: 4,
    flexShrink: 0,
  },
  itemImagePlaceholder: {
    width: 80,
    height: 80,
    backgroundColor: GRAY_LIGHT,
    borderRadius: 4,
    flexShrink: 0,
  },

  // Columns to the right of the image
  columns: {
    flex: 1,
    flexDirection: "row",
    gap: 16,
  },
  colItem: {
    flex: 2,
  },
  colMaterials: {
    flex: 1.4,
  },
  colNote: {
    flex: 1,
  },

  colLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: GRAY_DARK,
    marginBottom: 4,
  },
  colText: {
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
  },
  colTextMuted: {
    fontFamily: "Helvetica",
    color: GRAY_DARK,
    marginBottom: 2,
  },
  colTextMutedSpaced: {
    fontFamily: "Helvetica",
    color: GRAY_DARK,
    marginBottom: 7,
  },
  colTextMutedIndented: {
    fontFamily: "Helvetica",
    color: GRAY_DARK,
    marginBottom: 2,
    marginLeft: 7,
  },

  internalDivider: {
    borderBottomWidth: 1,
    borderBottomColor: "#F1F5F9",
    marginTop: 10,
    marginBottom: 10,
  },

  bottomZone: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  priceRightGroup: {
    flexDirection: "row",
    marginLeft: "auto",
    gap: 58,
  },
  priceCell: {
    flexShrink: 0,
  },
  priceCellLast: {
    flexShrink: 0,
    alignItems: "flex-end",
  },
  priceCellLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: GRAY_DARK,
    marginBottom: 3,
  },
  priceCellValue: {
    fontFamily: "Helvetica",
  },
  priceCellValueBold: {
    fontFamily: "Helvetica-Bold",
  },

  // ── Totals ────────────────────────────────────────────────────
  totalsSection: {
    marginTop: 20,
    alignItems: "flex-end",
  },
  subtotalLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: 230,
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: GRAY_BORDER,
  },
  subtotalLabel: {
    fontFamily: "Helvetica",
    fontSize: 9,
  },
  subtotalValue: {
    fontFamily: "Helvetica",
    fontSize: 9,
  },
  taxLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: 230,
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: GRAY_BORDER,
  },
  taxLabel: {
    fontFamily: "Helvetica",
    fontSize: 9,
  },
  taxValue: {
    fontFamily: "Helvetica",
    fontSize: 9,
  },
  balanceDueBox: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: BRAND_TEAL,
    paddingVertical: 9,
    paddingHorizontal: 16,
    borderRadius: 6,
    width: 230,
    marginTop: 6,
  },
  balanceDueLabel: {
    fontFamily: "Helvetica-Bold",
    color: "#FFFFFF",
    fontSize: 10,
    letterSpacing: 0.5,
  },
  balanceDueValue: {
    fontFamily: "Helvetica-Bold",
    color: "#FFFFFF",
    fontSize: 10,
  },

  // ── Page footer ───────────────────────────────────────────────
  footer: {
    position: "absolute",
    bottom: 24,
    left: 48,
    right: 48,
    borderTopWidth: 1,
    borderTopColor: GRAY_BORDER,
    paddingTop: 7,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    color: GRAY_DARK,
    fontSize: 7.5,
  },
});

export type CartPdfVariant = "quote" | "invoice";

type Props = {
  cart: Cart;
  imageMap: Record<string, string>;
  logoBase64?: string;
  variant?: CartPdfVariant;
};

const VARIANT_LABELS: Record<CartPdfVariant, string> = {
  quote: "QUOTE",
  invoice: "INVOICE",
};

function htmlToPlainText(html: string): string {
  return html
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function CartPdf({
  cart,
  imageMap,
  logoBase64,
  variant = "quote",
}: Props) {
  const variantLabel = VARIANT_LABELS[variant];
  const cartAsAny = cart as any;
  const lineItems = cart.lineItems;

  const totalPrice = lineItems.reduce((sum, item) => {
    const price = Number(item.itemJson.price || 0);
    const qty = item.quantity || 0;
    return sum + price * qty;
  }, 0);

  const generatedDate = formatDateLong(new Date());

  return (
    <Document
      title={`Cart ${variantLabel} – ${cartAsAny.name || cart.id}`}
      author="Tribeca Printworks"
    >
      <Page size="LETTER" style={s.page}>
        {/* ── Page header ── */}
        <View style={s.header}>
          <View>
            {logoBase64 ? (
              <Image style={s.logoImage} src={logoBase64} />
            ) : (
              <Text style={s.brandName}>TRIBECA PRINTWORKS</Text>
            )}
            <View style={s.contactInfo}>
              <Text style={s.contactText}>333 Hudson St, Rm 201, </Text>
              <Text style={s.contactText}>New York, NY 10013</Text>
              <Text style={s.contactText}>(212) 226-2722</Text>
              <Text style={s.contactText}>info@tribecaprintworks.com</Text>
            </View>
          </View>
          <View style={s.headerRight}>
            <Text style={s.docTitle}>{variantLabel}</Text>
            {cartAsAny.name && (
              <View style={s.metaRow}>
                <Text style={s.metaValue}>{cartAsAny.name}</Text>
              </View>
            )}
            <View style={s.metaRow}>
              <Text style={s.metaValue}>{generatedDate}</Text>
            </View>
            <View style={s.metaRow}>
              <Text style={s.metaLabel}>Items: </Text>
              <Text style={s.metaValue}>{lineItems.length}</Text>
            </View>
          </View>
        </View>

        <View style={s.dividerTop} />

        <Text style={s.sectionTitle}>Items</Text>

        {/* ── Item cards ── */}
        {lineItems.map(item => {
          const parsed = parseLineItem(item);
          const cartData = item.itemJson?.cartData || {};
          const filteredDescriptions = (
            cartData.conditionalFields || []
          ).filter(Boolean);
          const showUploadedFileName = !item.adminCustom;
          const uploadedFileName = item.itemJson?.fileName || "Unnamed upload";

          return (
            <View key={item.id} style={s.itemCard} wrap={false}>
              {/* Top zone */}
              <View style={s.topZone}>
                {/* Image — keyed by item ID, pre-fetched as base64 */}
                {imageMap[item.id] ? (
                  <Image style={s.itemImage} src={imageMap[item.id]} />
                ) : (
                  <View style={s.itemImagePlaceholder} />
                )}

                {/* Columns */}
                <View style={s.columns}>
                  {/* Item column */}
                  <View style={s.colItem}>
                    <Text style={s.colLabel}>Item</Text>
                    {parsed.title && (
                      <Text style={s.colText}>{parsed.title}</Text>
                    )}
                    {showUploadedFileName && (
                      <Text style={s.colTextMutedSpaced}>
                        {uploadedFileName}
                      </Text>
                    )}
                    {parsed.size && (
                      <Text style={s.colTextMutedIndented}>
                        • {parsed.size}
                      </Text>
                    )}
                    {parsed.type && (
                      <Text style={s.colTextMutedIndented}>
                        • {parsed.type}
                      </Text>
                    )}
                    {parsed.subtype && (
                      <Text style={s.colTextMutedIndented}>
                        • {parsed.subtype}
                      </Text>
                    )}
                    {filteredDescriptions.map((description, index) => (
                      <Text
                        key={`${item.id}-description-${index}`}
                        style={s.colTextMutedIndented}
                      >
                        • {description}
                      </Text>
                    ))}
                    {Number(item.itemJson?.sizing?.dimensions?.overallWidth) >
                      0 &&
                      Number(item.itemJson?.sizing?.dimensions?.overallHeight) >
                        0 && (
                        <Text style={s.colTextMutedIndented}>
                          • {item.itemJson.sizing.dimensions.overallWidth}"
                          &times;{" "}
                          {item.itemJson.sizing.dimensions.overallHeight}"
                        </Text>
                      )}
                    {item.itemJson.description &&
                      (() => {
                        const text = htmlToPlainText(item.itemJson.description);
                        return text ? (
                          <Text style={s.colTextMutedIndented}>• {text}</Text>
                        ) : null;
                      })()}
                  </View>

                  {/* Note column */}
                  <View style={s.colNote}>
                    <Text style={s.colLabel}>Note</Text>
                    <Text style={s.colTextMuted}>
                      {item.note ? item.note : "N/A"}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Internal divider */}
              <View style={s.internalDivider} />

              {/* Bottom zone — pricing row */}
              <View style={s.bottomZone}>
                <View style={s.priceCell}>
                  <Text style={s.priceCellLabel}>Quantity</Text>
                  <Text style={s.priceCellValue}>{item.quantity}</Text>
                </View>
                <View style={s.priceRightGroup}>
                  <View style={s.priceCell}>
                    <Text style={s.priceCellLabel}>Unit Price</Text>
                    <Text style={s.priceCellValue}>
                      {formatPrice(parsed.price)}
                    </Text>
                  </View>
                  <View style={s.priceCellLast}>
                    <Text style={s.priceCellLabel}>Total</Text>
                    <Text style={s.priceCellValueBold}>
                      {formatPrice(parsed.totalPrice)}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          );
        })}

        {/* ── Balance due ── */}
        <View style={s.totalsSection}>
          <View style={s.subtotalLine}>
            <Text style={s.subtotalLabel}>Item Total</Text>
            <Text style={s.subtotalValue}>{formatPrice(totalPrice)}</Text>
          </View>
          {variant === "invoice" && !cart.taxExempt && (
            <View style={s.taxLine}>
              <Text style={s.taxLabel}>Sales Tax (8.875%)</Text>
              <Text style={s.taxValue}>
                {formatPrice(totalPrice * 0.08875)}
              </Text>
            </View>
          )}
          <View style={s.balanceDueBox}>
            <Text style={s.balanceDueLabel}>BALANCE DUE</Text>
            <Text style={s.balanceDueValue}>
              {formatPrice(
                variant === "invoice" && !cart.taxExempt
                  ? totalPrice * 1.08875
                  : totalPrice
              )}
            </Text>
          </View>
        </View>

        {/* ── Page footer ── */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>Tribeca Printworks</Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
